// Admin → Photo Audit → Recover (per-form)
//
// Side-by-side view of one affected form's current DB photos vs the
// orphaned OneDrive files identified by the scan. The reviewer ticks
// which orphans to merge back into the form and optionally adds a
// caption for each. On confirm, a single POST to /recover writes them.
//
// Backed by: GET /api/admin/photo-audit/scan?form_type=...&only_affected=true
//            POST /api/admin/photo-audit/recover[?dry_run=true]
//
// No DB writes happen until the reviewer clicks Confirm in the dialog.

import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  ArrowLeft,
  RefreshCw,
  CheckCircle2,
  AlertTriangle,
  ShieldCheck,
  Database,
  CloudOff,
  Cloud,
  Camera,
} from 'lucide-react';
import { toast } from 'sonner';
import { SafeImage } from '../components/SafeImage';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const FILE_PROXY = (id) => `${API}/onedrive/file-proxy/${id}`;


export default function AdminPhotoAuditRecover() {
  const { formType, formId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [form, setForm]       = useState(null);     // { form_type, form_id, job_number, db_photo_count, onedrive_photo_count, orphans:[], ... }
  const [selections, setSelections] = useState({}); // onedrive_id -> { selected: bool, caption: string }
  const [confirming, setConfirming] = useState(false);
  const [dryRun, setDryRun]         = useState(null); // result of the dry-run preview
  const [committing, setCommitting] = useState(false);

  const authHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('fieldflow_token')}` },
  });

  // Initial load — re-runs the scan scoped to the right form type and finds this form.
  useEffect(() => {
    if (!formType || !formId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/admin/photo-audit/scan`, {
          ...authHeaders(),
          params: { form_type: formType, only_affected: false },
        });
        if (cancelled) return;
        const match = (r.data.forms || []).find(f => f.form_id === formId);
        if (!match) {
          toast.error(`Form ${formId} not found in latest scan results.`);
          setForm(null);
        } else {
          setForm(match);
          const initial = {};
          (match.orphans || []).forEach(o => {
            initial[o.onedrive_id] = { selected: true, caption: '' };
          });
          setSelections(initial);
        }
      } catch (e) {
        toast.error(`Scan failed: ${e.response?.data?.detail || e.message}`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [formType, formId]); // eslint-disable-line

  const selectedOrphans = useMemo(
    () => (form?.orphans || []).filter(o => selections[o.onedrive_id]?.selected),
    [form, selections]
  );

  const toggleAll = (val) => {
    const next = { ...selections };
    (form?.orphans || []).forEach(o => {
      next[o.onedrive_id] = { ...(next[o.onedrive_id] || { caption: '' }), selected: val };
    });
    setSelections(next);
  };

  const setCaption = (oid, caption) => {
    setSelections(prev => ({
      ...prev,
      [oid]: { ...(prev[oid] || { selected: true }), caption },
    }));
  };

  const buildPayload = () => ({
    form_type: form.form_type,
    form_id:   form.form_id,
    orphans: selectedOrphans.map(o => ({
      onedrive_id: o.onedrive_id,
      caption:     (selections[o.onedrive_id]?.caption || '').trim(),
    })),
  });

  const openConfirm = async () => {
    if (selectedOrphans.length === 0) {
      toast.warning('Select at least one orphan to recover.');
      return;
    }
    try {
      const r = await axios.post(
        `${API}/admin/photo-audit/recover`,
        buildPayload(),
        { ...authHeaders(), params: { dry_run: true } }
      );
      setDryRun(r.data);
      setConfirming(true);
    } catch (e) {
      toast.error(`Preview failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  const doCommit = async () => {
    setCommitting(true);
    try {
      const r = await axios.post(
        `${API}/admin/photo-audit/recover`,
        buildPayload(),
        { ...authHeaders(), params: { dry_run: false } }
      );
      const added   = r.data.added?.length || 0;
      const skipped = (r.data.skipped_already_present?.length || 0) + (r.data.skipped_no_longer_in_onedrive?.length || 0);
      toast.success(`Recovered ${added} photo${added === 1 ? '' : 's'}` + (skipped ? ` (${skipped} skipped).` : '.'));
      setConfirming(false);
      // Reload the page so the read-only "current DB photos" panel reflects the merge.
      setForm(null);
      setSelections({});
      // Re-scan; small delay so the user sees the toast.
      setTimeout(() => {
        navigate(0); // hard refresh of this route
      }, 800);
    } catch (e) {
      toast.error(`Recovery failed: ${e.response?.data?.detail || e.message}`);
      setConfirming(false);
    } finally {
      setCommitting(false);
    }
  };

  return (
    <div className="container mx-auto py-6 px-4 max-w-7xl space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Link to="/admin/photo-audit" className="text-blue-600 hover:underline flex items-center gap-1 text-sm" data-testid="back-to-audit">
            <ArrowLeft className="w-4 h-4" /> Back to audit
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Camera className="w-6 h-6 text-blue-600" /> Photo Recovery
            </h1>
            <p className="text-sm text-slate-600 mt-0.5">
              {form ? (
                <>
                  <span className="font-semibold text-slate-800">{form.job_number || '(unnamed)'}</span>
                  {' · '}
                  <span className="font-mono text-xs">{form.form_type}</span>
                  {' · '}
                  status: <span className="font-mono text-xs">{form.status_field || '—'}</span>
                </>
              ) : 'Loading…'}
            </p>
          </div>
        </div>
        <Badge variant="outline" className="border-emerald-300 bg-emerald-50 text-emerald-700 px-3 py-1">
          <ShieldCheck className="w-3 h-3 mr-1" /> Audited writes — every recovery logged
        </Badge>
      </div>

      {loading && (
        <Card>
          <CardContent className="py-10 text-center text-slate-500">
            <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
            <p className="text-sm">Loading form audit…</p>
          </CardContent>
        </Card>
      )}

      {!loading && !form && (
        <Card>
          <CardContent className="py-10 text-center text-slate-500">
            <p className="text-sm">Form not found in the latest audit results.</p>
            <Link to="/admin/photo-audit" className="text-blue-600 hover:underline text-sm">Return to audit</Link>
          </CardContent>
        </Card>
      )}

      {!loading && form && (
        <>
          {/* Summary stats */}
          <Card>
            <CardContent className="py-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <StatTile icon={<Database className="w-4 h-4" />} label="Currently in DB"    value={form.db_photo_count}       tone="slate" testid="stat-db-count" />
                <StatTile icon={<Cloud    className="w-4 h-4" />} label="In OneDrive"        value={form.onedrive_photo_count} tone="slate" testid="stat-od-count" />
                <StatTile icon={<AlertTriangle className="w-4 h-4" />} label="Orphans (will recover)" value={form.orphan_count}   tone="amber" testid="stat-orphan-count" />
                <StatTile icon={<CloudOff className="w-4 h-4" />} label="Dangling DB records" value={form.dangling_count}      tone={form.dangling_count ? 'red' : 'slate'} testid="stat-dangling-count" />
              </div>
            </CardContent>
          </Card>

          {/* Orphan picker */}
          <Card data-testid="orphan-picker">
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <CardTitle className="text-base">
                  Orphaned OneDrive files ({form.orphans?.length || 0})
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => toggleAll(true)}  data-testid="select-all-btn">
                    Select all
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => toggleAll(false)} data-testid="deselect-all-btn">
                    Deselect all
                  </Button>
                  <Button
                    onClick={openConfirm}
                    disabled={selectedOrphans.length === 0}
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="preview-merge-btn"
                  >
                    Preview merge ({selectedOrphans.length})
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {(!form.orphans || form.orphans.length === 0) ? (
                <p className="text-sm text-slate-500 py-4">No orphans for this form.</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {form.orphans.map(o => (
                    <OrphanCard
                      key={o.onedrive_id}
                      orphan={o}
                      selection={selections[o.onedrive_id] || { selected: true, caption: '' }}
                      onToggle={(val) =>
                        setSelections(prev => ({
                          ...prev,
                          [o.onedrive_id]: { ...(prev[o.onedrive_id] || { caption: '' }), selected: val },
                        }))
                      }
                      onCaption={(cap) => setCaption(o.onedrive_id, cap)}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Current DB photos — read-only, for context */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                Currently in database ({form.db_photo_count}) <span className="text-xs font-normal text-slate-500">— read-only</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-slate-500 mb-3">
                These are the photo records already saved on this form. They are shown here for context — the recovery
                only appends the selected orphans above; it does not touch existing records.
              </p>
              <p className="text-xs text-slate-500 italic">
                (Full thumbnail comparison is intentionally light to keep this page snappy. Open the form itself
                at <Link to={formViewPath(form)} className="text-blue-600 hover:underline">{formViewPath(form)}</Link> to see
                the existing photos in their normal context.)
              </p>
            </CardContent>
          </Card>
        </>
      )}

      {/* Confirm dialog */}
      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-blue-600" />
              Confirm merge
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm text-slate-700">
                <p>
                  About to <span className="font-semibold">append {dryRun?.added?.length || 0} photo
                  {(dryRun?.added?.length || 0) === 1 ? '' : 's'}</span> to
                  {' '}<span className="font-mono">{form?.form_type}</span> form
                  {' '}<span className="font-mono">{form?.job_number || form?.form_id}</span>.
                </p>
                {form?.form_type === 'soft-digs' && (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 rounded">
                    Soft Dig: the recovered photos will be added as a new reference point named
                    <span className="font-semibold"> "Recovered photos"</span>. You can rearrange them across
                    existing reference points by editing the form afterwards.
                  </p>
                )}
                {dryRun?.skipped_already_present?.length > 0 && (
                  <p className="text-xs text-slate-500">
                    {dryRun.skipped_already_present.length} skipped — already in DB.
                  </p>
                )}
                {dryRun?.skipped_no_longer_in_onedrive?.length > 0 && (
                  <p className="text-xs text-red-600">
                    {dryRun.skipped_no_longer_in_onedrive.length} skipped — file no longer on OneDrive.
                  </p>
                )}
                <p className="text-xs text-slate-500">
                  Each recovered photo is logged in <span className="font-mono">db.photo_recovery_log</span>.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={committing} data-testid="recovery-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); doCommit(); }}
              disabled={committing || (dryRun?.added?.length || 0) === 0}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="recovery-confirm-btn"
            >
              {committing ? (<><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Merging…</>) : 'Merge now'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}


function OrphanCard({ orphan, selection, onToggle, onCaption }) {
  const created = orphan.created ? new Date(orphan.created).toLocaleString() : '—';
  const size    = orphan.size != null ? `${(orphan.size / 1024).toFixed(0)} KB` : '—';
  return (
    <div
      className={`border rounded-lg overflow-hidden transition ${selection.selected ? 'border-blue-400 bg-blue-50/30' : 'border-slate-200 bg-white opacity-70'}`}
      data-testid={`orphan-card-${orphan.onedrive_id}`}
    >
      <SafeImage
        src={FILE_PROXY(orphan.onedrive_id)}
        alt={orphan.name}
        className="w-full h-48 object-cover bg-slate-100"
        loading="lazy"
        decoding="async"
      />
      <div className="p-3 space-y-2">
        <div className="flex items-start gap-2">
          <input
            type="checkbox"
            checked={selection.selected}
            onChange={e => onToggle(e.target.checked)}
            className="mt-1"
            data-testid={`orphan-checkbox-${orphan.onedrive_id}`}
          />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-mono text-slate-700 truncate" title={orphan.name}>{orphan.name}</p>
            <p className="text-xs text-slate-500">{created} · {size}</p>
            {orphan.webUrl && (
              <a href={orphan.webUrl} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline">open in OneDrive ↗</a>
            )}
          </div>
        </div>
        <Input
          placeholder="Optional caption (lost during the bug)"
          value={selection.caption || ''}
          onChange={e => onCaption(e.target.value)}
          disabled={!selection.selected}
          className="text-xs h-8"
          data-testid={`orphan-caption-${orphan.onedrive_id}`}
        />
      </div>
    </div>
  );
}


function StatTile({ icon, label, value, tone = 'slate', testid }) {
  const tones = {
    slate:   'bg-slate-50 text-slate-900 border-slate-200',
    amber:   'bg-amber-50 text-amber-900 border-amber-200',
    emerald: 'bg-emerald-50 text-emerald-900 border-emerald-200',
    red:     'bg-red-50 text-red-900 border-red-200',
  }[tone];
  return (
    <div className={`rounded-md border px-3 py-2 ${tones}`} data-testid={testid}>
      <div className="text-xs uppercase tracking-wide font-medium opacity-70 flex items-center gap-1">{icon} {label}</div>
      <div className="text-2xl font-bold tabular-nums">{value}</div>
    </div>
  );
}


function formViewPath(form) {
  // Best-effort deep-link into the form itself. The real route paths in
  // App.js are /precon/:id, /locate/:id, /soft-dig/:id (singular).
  const singular = ({ 'precons': 'precon', 'locates': 'locate', 'soft-digs': 'soft-dig' })[form.form_type] || form.form_type;
  return `/${singular}/${form.form_id}`;
}
